import { cn } from '@cobliteam/colib-ui/cn';
import Typography from '@cobliteam/ui/components/Typography';
import { MousePointer2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useCursorFlowStore from '../../store/useCursorFlowStore';

/** Cursor travel time between steps (kept in sync with the CSS transition below). */
const MOVE_MS = 750;
/** How long to keep an element-locating poll alive before skipping the step. */
const FIND_TIMEOUT_MS = 4000;

interface Point {
  x: number;
  y: number;
}

/**
 * Plays a choreographed cursor sequence (move → fake click → type → simulated circle draw)
 * to demonstrate a task on the destination page. Visual only and non-interactive
 * (`pointer-events-none`); the one exception is `type`, which writes into a real input so the
 * field visibly fills, and optional `realClick` steps.
 */
const CursorFlow = () => {
  const steps = useCursorFlowStore((state) => state.steps);
  const stop = useCursorFlowStore((state) => state.stop);

  const [cursor, setCursor] = useState<Point | null>(null);
  const [pulse, setPulse] = useState<{ x: number; y: number; id: number } | null>(null);
  const [circle, setCircle] = useState<{ cx: number; cy: number; r: number } | null>(null);
  const [label, setLabel] = useState<string | null>(null);
  // While orbiting we drive the cursor with rAF, so the easing transition must be off (it would lag).
  const [orbiting, setOrbiting] = useState(false);

  // Abort on Escape.
  useEffect(() => {
    if (!steps) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') stop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [steps, stop]);

  // Run the sequence.
  useEffect(() => {
    if (!steps || steps.length === 0) return;

    let cancelled = false;
    const timers: number[] = [];
    let pulseId = 0;

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(resolve, ms);
        timers.push(id);
      });

    const waitFor = (selector: string) =>
      new Promise<HTMLElement | null>((resolve) => {
        const startedAt = performance.now();
        const tick = () => {
          if (cancelled) return resolve(null);
          const el = document.querySelector<HTMLElement>(selector);
          if (el) return resolve(el);
          if (performance.now() - startedAt > FIND_TIMEOUT_MS) return resolve(null);
          requestAnimationFrame(tick);
        };
        tick();
      });

    const typeInto = async (input: HTMLInputElement, text: string) => {
      input.focus();
      const setValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
      for (let i = 1; i <= text.length; i++) {
        if (cancelled) return;
        setValue?.call(input, text.slice(0, i));
        input.dispatchEvent(new Event('input', { bubbles: true }));
        await sleep(45);
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const run = async () => {
      let prev: Point = { x: window.innerWidth / 2, y: window.innerHeight - 60 };
      setCursor(prev);
      await sleep(300);

      for (const step of steps) {
        if (cancelled) return;
        setLabel(step.label ?? null);

        // Resolve the target point.
        let target: Point | null = null;
        if (step.originSelector) {
          const el = document.querySelector<HTMLElement>(step.originSelector);
          if (el) {
            const rect = el.getBoundingClientRect();
            target = { x: rect.left + rect.width / 2 + (step.offset?.x ?? 0), y: rect.top + rect.height / 2 + (step.offset?.y ?? 0) };
          }
        } else if (step.selector) {
          const el = await waitFor(step.selector);
          if (cancelled) return;
          if (el) {
            const rect = el.getBoundingClientRect();
            target = { x: rect.left + rect.width / 2 + (step.offset?.x ?? 0), y: rect.top + rect.height / 2 + (step.offset?.y ?? 0) };
          }
        }
        if (!target) continue;

        // Orbit: move the cursor itself around the anchor in a circular path (rAF-driven, no easing).
        if (step.orbit) {
          const { radius, turns = 2 } = step.orbit;
          const center = target;
          const orbitMs = step.holdMs ?? 1500;
          setOrbiting(true);
          // Ease into the circle's starting point (top) before spinning.
          const start = { x: center.x, y: center.y - radius };
          setCursor(start);
          await sleep(MOVE_MS);
          if (cancelled) return;
          const startedAt = performance.now();
          await new Promise<void>((resolve) => {
            const spin = () => {
              if (cancelled) return resolve();
              const progress = Math.min((performance.now() - startedAt) / orbitMs, 1);
              const angle = progress * turns * 2 * Math.PI - Math.PI / 2;
              setCursor({ x: center.x + radius * Math.cos(angle), y: center.y + radius * Math.sin(angle) });
              if (progress >= 1) return resolve();
              requestAnimationFrame(spin);
            };
            spin();
          });
          if (cancelled) return;
          setOrbiting(false);
          prev = { x: center.x, y: center.y - radius };
          continue;
        }

        // Simulated radius drag: grow a circle from the previous point to this one.
        if (step.dragCircle) {
          const cx = prev.x;
          const cy = prev.y;
          const radius = Math.hypot(target.x - cx, target.y - cy);
          setCircle({ cx, cy, r: 0 });
          requestAnimationFrame(() => {
            if (!cancelled) setCircle({ cx, cy, r: radius });
          });
        }

        // Travel to the target.
        setCursor(target);
        await sleep(MOVE_MS);
        if (cancelled) return;
        prev = target;

        // Perform the step's action.
        if (step.action === 'type' && step.selector && step.text) {
          const input = document.querySelector<HTMLInputElement>(step.selector);
          if (input) await typeInto(input, step.text);
        } else if (step.action === 'click' || step.action === 'finish') {
          pulseId += 1;
          setPulse({ x: target.x, y: target.y, id: pulseId });
          if (step.realClick && step.selector) {
            document.querySelector<HTMLElement>(step.selector)?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
          }
        }

        await sleep(step.holdMs ?? 400);
      }

      if (cancelled) return;
      await sleep(1400);
      if (!cancelled) stop();
    };

    run();
    return () => {
      cancelled = true;
      for (const id of timers) window.clearTimeout(id);
    };
  }, [steps, stop]);

  // Reset visuals when the flow ends.
  useEffect(() => {
    if (!steps) {
      setCursor(null);
      setPulse(null);
      setCircle(null);
      setLabel(null);
      setOrbiting(false);
    }
  }, [steps]);

  if (!(steps && cursor)) return null;

  return createPortal(
    <div className='pointer-events-none fixed inset-0 z-tooltip'>
      {/* Simulated circle being drawn on the map */}
      {circle && (
        <div
          className='absolute rounded-full border-2 border-primary border-solid bg-primary/20 transition-all duration-700 ease-out'
          style={{ left: circle.cx - circle.r, top: circle.cy - circle.r, width: circle.r * 2, height: circle.r * 2 }}
        />
      )}
      {/* Click ripple */}
      {pulse && (
        <span key={pulse.id} className='absolute size-6 animate-ping rounded-full border-2 border-primary border-solid' style={{ left: pulse.x - 12, top: pulse.y - 12 }} />
      )}
      {/* Label near the cursor */}
      {label && (
        <div className='absolute rounded-md bg-gray-12 px-2 py-1 shadow-md' style={{ left: cursor.x + 18, top: cursor.y + 18 }}>
          <Typography variant='text-sm' className='whitespace-nowrap text-white'>
            {label}
          </Typography>
        </div>
      )}
      {/* The animated pointer */}
      <div
        className={cn('absolute drop-shadow-md', !orbiting && 'transition-transform duration-700 ease-out')}
        style={{ transform: `translate(${cursor.x - 2}px, ${cursor.y - 2}px)` }}
      >
        <MousePointer2 size={28} className='fill-primary text-white' aria-hidden />
      </div>
    </div>,
    document.body
  );
};

export default CursorFlow;
